import { useLang } from 'hooks/lang';

import { ClientBalanceEntry, clientBalancesStatusConfig, loaders } from 'entities';

import { Column, Row, Alert, Tag } from 'components/Blocks';

import { PlusIcon } from 'components/Icons';

import { FormControlDispatchAction } from 'hooks';

import { extractFixedInvoiceData, verifyIfBalanceCanBeAdded } from './utils';
import { formatCurrency } from 'utils/text';

import { BaseInvoiceProps, InvoiceCreationProps } from './types';

interface Props {
  children?: never;
  entities: BaseInvoiceProps[];
  state: InvoiceCreationProps;
  dispatch: FormControlDispatchAction<InvoiceCreationProps>;
  total: string;
}

export function ClientBalances({ dispatch, entities, state, total }: Props): JSX.Element | null {
  const { t } = useLang();

  const { client, currency } = extractFixedInvoiceData(entities);

  const { data } = loaders.clients.balanceEntries.use({
    params: {
      id: client!,
      status: clientBalancesStatusConfig.active.code,
    },

    blockLoad: !client,

    defaultValue: [],
  });

  const eligible = data.filter((balance) =>
    [
      balance.currency === currency,
      balance.type === 'OVERPAYMENT' ? Number(balance.delta) < Number(total) : true,
      !state.clientBalances.find(({ id }) => id === balance.id),
    ].every(Boolean),
  );

  if (!eligible.length && !state.clientBalances.length) return null;

  const addBalance = (balance: ClientBalanceEntry): void => {
    dispatch({
      clientBalances: ({ clientBalances }) =>
        clientBalances.find((existing) => existing.id === balance.id)
          ? clientBalances
          : [...clientBalances, balance],
    });
  };

  return (
    <Alert level="info" title={t('client-has-active-balance-entries')}>
      <Column>
        <span>{t('client-balance-invoice-explanation')}</span>

        {!!eligible.length && (
          <Row>
            {eligible.map((balance) => (
              <Tag
                actionIcon={<PlusIcon />}
                key={balance.id}
                name={`${formatCurrency(balance.delta, currency)}`}
                color={balance.type === 'OVERPAYMENT' ? 'success' : 'destructive'}
                action={
                  verifyIfBalanceCanBeAdded({ state, total, balance })
                    ? () => addBalance(balance)
                    : undefined
                }
              />
            ))}
          </Row>
        )}
      </Column>
    </Alert>
  );
}
